/* eslint-disable react/no-array-index-key */
import { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { ClaimCard } from 'components/ClaimCard';
import { useDispatch, useSelector } from 'react-redux';
import { useHistoryWithHost } from 'routes/useHistoryWithHost';
import { RouterContext } from 'routes/RouterProvider';

import { useLocation } from 'react-router-dom';
import { ApplicationState } from 'store/store.inteface';
import {
  getClaimsByTypeAction,
  setTimelineClaimId,
} from 'store/modules/claims/actions';
import { useDebounce, useWindowSize } from 'hooks';
import { IClaim } from 'store/modules/claims/types';
import { LoadingSkeleton } from 'components/LoadingSkeleton';
import { Wrapper, Title, GridClaims } from './styles';
import { ShowMoreButton } from './ShowMoreButton';
import { SearchTerms } from './SearchTerms';

export interface ClaimWrapperProps {
  title: string;
  type: 'active' | 'completed';
}

export const ClaimWrapper = ({ type, title }: ClaimWrapperProps) => {
  const [showMore, setShowMore] = useState(false);

  const [mountedClaims, setMountedClaims] = useState<Array<IClaim | undefined>>(
    [],
  );

  const [loading, setLoading] = useState(false);
  const [searchTerms, setSearchTerms] = useState('');

  const history = useHistoryWithHost();
  const location = useLocation();
  const routes = useContext(RouterContext);
  const dispatch = useDispatch();
  const { breakpoints, windowSize } = useWindowSize();
  const betweenSmMd = useMemo(
    () => windowSize.width && windowSize.width > 0 && windowSize.width <= 1110,
    [windowSize.width],
  );
  const claims = useSelector((state: ApplicationState) => state.claims);
  const claimsType = claims[type];

  const { stores } = useSelector(
    (state: ApplicationState) => state.storesContext,
  );

  function onChangeSearch(value: string) {
    setSearchTerms(value);
  }
  useDebounce(onChangeSearch, 500);

  const getClaim = useCallback(
    () =>
      dispatch(
        getClaimsByTypeAction({
          type,
          searchTerms,
          limit: breakpoints.isXl || !!betweenSmMd ? 6 : 4,
          showMore: showMore || !!betweenSmMd,
        }),
      ),
    [dispatch, breakpoints.isXl, betweenSmMd, showMore, searchTerms, type],
  );

  useEffect(() => {
    setLoading(false);
    if (
      (stores.length > 0 && breakpoints.isXl !== undefined) ||
      (stores.length > 0 && !claimsType.responseValid)
    ) {
      getClaim();
      setLoading(true);
    }
  }, [getClaim, breakpoints.isXl, claimsType.responseValid, stores]);

  useEffect(() => {
    const claimResults = claimsType.results;
    if (
      betweenSmMd ||
      (breakpoints.isXl && claimResults.length > 6) ||
      (!breakpoints.isXl && claimResults.length > 4)
    ) {
      setMountedClaims(claimResults);
      return;
    }
    const claimsIndex = breakpoints.isXl ? [0, 1, 2, 3, 4, 5] : [0, 1, 2, 3];

    const claimsByIndex = claimsIndex.map(index =>
      claimResults[index] ? claimResults[index] : undefined,
    );

    setMountedClaims(claimsByIndex);
  }, [claimsType, breakpoints.isXl, betweenSmMd]);

  useEffect(() => {
    if (
      location.state &&
      type === 'active' &&
      (routes.from.includes('openClaim') || routes.from.includes('openclaim'))
    ) {
      dispatch(setTimelineClaimId(location.state.id));
    }
  }, []);

  return (
    <Wrapper>
      <header>
        <Title>{title}</Title>
        <SearchTerms onChange={onChangeSearch} value={searchTerms} />
      </header>
      <GridClaims>
        {mountedClaims.map((claim, index) => {
          if (claim) {
            return (
              <ClaimCard
                key={claim.id}
                id={claim.id}
                title={claim.number}
                status={claim.status}
                storeName={claim.franchise?.name}
                storeNumber={claim.franchise?.code}
                type={claim.subCoverage?.name}
                comunicationDate={claim.updatedAt}
                claimWithoutUpdate={claim?.claimWithoutUpdate}
                onClickTimeline={() => dispatch(setTimelineClaimId(claim.id))}
                onClickProccess={
                  () => history.push(`/claim/${claim.id}/process`)
                  // eslint-disable-next-line react/jsx-curly-newline
                }
              />
            );
          }
          const skeletonKey = `Skeleton-${index}`;
          return (
            <LoadingSkeleton loading={!loading} count={10} key={skeletonKey}>
              <ClaimCard />
            </LoadingSkeleton>
          );
        })}
      </GridClaims>
      <footer>
        <ShowMoreButton
          setShowMore={setShowMore}
          hasMore={claimsType.hasMore}
          size={claimsType.results.length}
        />
      </footer>
    </Wrapper>
  );
};
